// numberGrouping.ts — Zahlformat und Caret-Arithmetik der deutschen Betragsfelder
// (Kennzahlen-Reiter, Erfassen-Wizard).
//
// Bewusst REACT-FREI und ohne DOM-Zugriff: die Komponenten verdrahten diese
// Funktionen nur noch (Text + Cursorposition rein, Text + Cursorposition raus).
// Die Regeln selbst liegen zentral hier und sind damit in der vitest-Default-
// Umgebung „node" testbar — eine Caret-Regression ist sonst nur per jsdom und
// user-event zu sehen, also genau dort, wo Tests am teuersten und wackeligsten
// sind.
//
// ZAHLENFORMAT (streng de-DE): Komma = Dezimaltrenner, Punkt = Tausenderpunkt.
// Diese Festlegung gilt ausnahmslos (siehe `sanitizeGerman`) — genau wie im
// Ausdrucks-Parser `calcExpression.ts`, damit dieselbe Eingabe im Rechner und im
// Betragsfeld nicht zwei verschiedene Zahlen ergibt.

/** Ziffer oder Dezimalkomma — die Zeichen, an denen der Cursor „festhält". */
const SIGNIFICANT_RE = /[\d,]/;

/** Alle Leerzeichenformen, die in formatierten Beträgen vorkommen (U+00A0/U+202F aus toLocaleString). */
const LEADING_SPACE_CLASS = "[\\s\\u00A0\\u202F]*";

export const stripFormatting = (raw: string): string => {
  // Akzeptiere Punkte (Tausender) und Komma (Dezimal), reduziere auf Zahlen + Komma.
  // Beispiel: "1.234.567,89" → "1234567,89"
  return raw.replace(/\./g, "").trim();
};

/**
 * Autoritative Bereinigung für den LIVE-Pfad: liefert aus beliebigem Feldinhalt
 * die kanonische Rohform „[-]Ziffern[,Ziffern]".
 *
 * `stripFormatting` taugt dafür nicht: es entfernt nur Punkte und trimmt. „12a3€"
 * überlebt dort im State und fällt heute bloß nicht auf, weil erst der Blur über
 * `parseEur` aufräumt. Wird bei jedem Tastendruck neu formatiert, muss die
 * Bereinigung genauso bei jedem Tastendruck greifen — sonst wandert Müll in den
 * angezeigten Text und die Caret-Rechnung zählt Zeichen, die es nach dem Blur
 * nicht mehr gibt.
 *
 * Umgesetzt als WHITELIST (alles außer Ziffer und Komma fällt weg) statt als
 * Liste verbotener Zeichen: Leerzeichen, geschützte Leerzeichen (U+00A0), schmale
 * geschützte Leerzeichen (U+202F), Währungssymbole („€", „EUR", „CHF"),
 * Apostrophe (schweizerischer Tausendertrenner) und alle Tausenderpunkte sind
 * damit erledigt — und auch jedes exotische Zeichen, das aus einer Zwischenablage
 * noch kommen mag.
 *
 * Zwei bewusste Festlegungen:
 * - Es bleibt genau EIN Dezimalkomma, und zwar das ERSTE. Ein zweites getipptes
 *   Komma wird so verworfen (aus „1,50" + „," wird wieder „1,50"), ohne dass es
 *   dafür eine eigene Regel braucht.
 *
 *   Das ERSTE und nicht das letzte, und das ist der Unterschied zwischen „nichts
 *   passiert" und einem stillen Datenfehler: mit „das letzte gewinnt" wanderte
 *   bei jedem weiteren Komma alles davor in den Integer-Teil — aus „1.000,5"
 *   plus einem am Ende getippten Komma würde „10.005" und damit ein zehnfach zu
 *   großer Betrag. Genau das passiert im Alltag, weil das zweite Komma sichtbar
 *   folgenlos bleibt und die Taste deshalb ein zweites Mal gedrückt wird.
 *
 *   Bewusste Kehrseite: ein LINKS des Dezimalkommas gesetztes Komma gewinnt
 *   (aus „1.000,5" mit Cursor hinter „1.0" wird „10,005") — dort ist es das
 *   erste. Das ist gewollt: der Nutzer hat den Cursor eigens dorthin gesetzt und
 *   verschiebt den Trenner absichtlich.
 * - Ein Minus zählt nur, wenn es VORNE steht; „1-2" ist keine negative Zahl.
 *   Das Vorzeichen eines bestehenden Betrags wird deshalb von `keepLeadingSign`
 *   festgehalten, bevor hier bereinigt wird.
 *
 * STRENG de-DE, ohne Heuristik: eine englische Eingabe wie „1,234,567.89" ergibt
 * hier „1,23456789" — also sichtbaren Unsinn statt einer geratenen Zahl. Genau so
 * gewollt: der Nutzer sieht den Fehler sofort im Feld und korrigiert ihn. Eine
 * Ratemechanik (etwa „Punkt hinter Dreiergruppe = Tausender, sonst Dezimal")
 * würde dieselbe Eingabe je nach Ziffernzahl unterschiedlich deuten und in den
 * Fällen, in denen sie falsch rät, still eine um Größenordnungen falsche Zahl
 * speichern.
 */
export const sanitizeGerman = (raw: string): string => {
  if (!raw) return "";
  const negative = new RegExp(`^${LEADING_SPACE_CLASS}-`).test(raw);
  const kept = raw.replace(/[^\d,]/g, "");
  const firstComma = kept.indexOf(",");
  // Vor dem ersten Komma kann nach `indexOf` keines mehr stehen; dahinter fallen
  // alle weiteren weg.
  const body =
    firstComma < 0
      ? kept
      : `${kept.slice(0, firstComma)},${kept.slice(firstComma + 1).replace(/,/g, "")}`;
  if (!negative) return body;
  // „-" allein bleibt stehen: das Vorzeichen darf vor der ersten Ziffer getippt werden.
  return `-${body}`;
};

/**
 * Hält das Vorzeichen an Position 0 fest, wenn davor eingefügt wurde.
 *
 * Im angezeigten Text steht das Minus IMMER ganz vorne — dafür sorgt
 * `sanitizeGerman`, das ein Minus nur dort als Vorzeichen zählt. Setzt der Nutzer
 * den Cursor davor und tippt eine Ziffer, rutscht das Vorzeichen aber nach rechts
 * („-1.234" + „9" an Position 0 → „9-1.234"), und die Bereinigung würfe es
 * ersatzlos weg: aus einem Verlust von 1.234 € würden still +91.234 €. Der Cursor
 * landet dort auch nicht nur versehentlich — beide Felder sind rechtsbündig, links
 * vom Text steht viel leerer Platz, und Pos1 tut dasselbe.
 *
 * Erkannt wird der Fall daran, dass der bisherige Feldinhalt INKLUSIVE Vorzeichen
 * ungeschnitten im neuen Inhalt steht und nur etwas davor gerutscht ist. Damit
 * bleiben die beiden anderen Wege unangetastet: ein gelöschtes Minus („-1.234" →
 * „1.234") macht den Betrag wie erwartet positiv, und ein eingefügtes „12-15" in
 * ein positives Feld bleibt positiv — die Regel „ein Minus zählt nur vorne" gilt
 * weiter, sie wird hier nur nicht durch eine Einfügung ausgehebelt.
 *
 * Der Cursor wandert mit: alles links des Vorzeichens rückt eine Stelle nach
 * rechts, wenn das Minus nach vorne zurückspringt.
 */
export const keepLeadingSign = (
  previous: string,
  raw: string,
  caret: number,
): { text: string; caret: number } => {
  if (!previous.startsWith("-")) return { text: raw, caret };
  const at = raw.indexOf("-");
  if (at <= 0 || raw.slice(at) !== previous) return { text: raw, caret };
  return {
    text: `-${raw.slice(0, at)}${previous.slice(1)}`,
    caret: caret <= at ? caret + 1 : caret,
  };
};

/**
 * Gruppiert den Integer-Teil einer bereits bereinigten Rohform mit Tausenderpunkten.
 *
 * Ein NACHLAUFENDES Komma bleibt dabei erhalten: beim Tippen von „1000," ist der
 * Dezimalteil noch leer, und würde das Komma hier verschwinden, könnte der Nutzer
 * nie einen Nachkommaanteil eintippen — die Neuformatierung fräße das Trennzeichen
 * bei jedem Tastendruck weg. Erst der Blur normalisiert es weg.
 */
export const formatGrouped = (sanitized: string): string => {
  if (sanitized === "" || sanitized === "-") return sanitized;
  const negative = sanitized.startsWith("-");
  const body = negative ? sanitized.slice(1) : sanitized;
  const commaAt = body.indexOf(",");
  const intDigits = (commaAt < 0 ? body : body.slice(0, commaAt)).replace(/\D/g, "");
  const frac = commaAt < 0 ? undefined : body.slice(commaAt + 1).replace(/\D/g, "");
  const grouped = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const sign = negative ? "-" : "";
  return frac === undefined ? `${sign}${grouped}` : `${sign}${grouped},${frac}`;
};

/** Ziffern und Kommas links des Cursors — das Maß, an dem die Position hängt. */
const countSignificant = (text: string): number => {
  let n = 0;
  for (const ch of text) if (SIGNIFICANT_RE.test(ch)) n++;
  return n;
};

/**
 * Herz der Live-Formatierung: bereinigt + gruppiert den Feldinhalt und rechnet
 * die Cursorposition mit.
 *
 * Zeichenpositionen lassen sich nicht übernehmen — beim Tippen von „1000" wächst
 * der Text durch den eingefügten Punkt auf „1.000", der Cursor müsste also von 4
 * auf 5 springen; beim Löschen umgekehrt. Deshalb wird nicht die Position
 * transportiert, sondern die ANZAHL SIGNIFIKANTER ZEICHEN links davon: Ziffern
 * plus das Komma. Das Komma zählt absichtlich mit, damit der Cursor nach dem
 * Tippen von „," dahinter stehen bleibt und nicht davor zurückfällt. Das
 * Vorzeichen zählt NICHT mit — es ist keine Stelle, und ein zwischenzeitlich
 * gesetztes Minus soll den Cursor nicht verschieben.
 *
 * Die Tausenderpunkte sind damit für die Navigation unsichtbar: der Cursor landet
 * immer hinter derselben Ziffer wie vorher, egal wie sich die Gruppierung
 * verschoben hat.
 */
export const reformatWithCaret = (
  rawValue: string,
  caret: number,
): { text: string; caret: number } => {
  const clamped = Math.max(0, Math.min(caret, rawValue.length));
  const significant = countSignificant(rawValue.slice(0, clamped));
  const text = formatGrouped(sanitizeGerman(rawValue));
  if (significant === 0) {
    // Nichts Signifikantes links: an den Anfang — aber hinter ein Minus, sonst
    // würde die nächste Ziffer VOR dem Vorzeichen landen.
    return { text, caret: text.startsWith("-") ? Math.min(1, text.length) : 0 };
  }
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (!SIGNIFICANT_RE.test(text[i])) continue;
    seen++;
    if (seen === significant) return { text, caret: i + 1 };
  }
  // Weniger signifikante Zeichen als vorher (z. B. verworfenes zweites Komma) → ans Ende.
  return { text, caret: text.length };
};

/**
 * Vorzeichen umschalten. Das Minus kann nur an Position 0 stehen und nur einmal;
 * ein erneuter Aufruf entfernt es wieder (Toggle, kein „immer negativ").
 *
 * Bewusst eine eigene Funktion statt „Minus tippen": in einer rechtsbündigen
 * Betragszelle steht der Cursor beim Tippen praktisch nie vorne, ein dort
 * eingegebenes „-" wäre also meistens ein Minus in der Mitte — und das wirft
 * `sanitizeGerman` weg.
 */
export const toggleSign = (raw: string): string => {
  const negative = raw.startsWith("-");
  const body = raw.replace(/-/g, "");
  return negative ? body : `-${body}`;
};

/**
 * EINE enge Regel gegen die häufigste Reibung: steht genau „0" im Feld und wird
 * an Position 1 eine Ziffer eingefügt, verschwindet die Null („0" + „5" → „5").
 *
 * Bewusst KEINE allgemeine Nullen-Normalisierung. Ein Live-Pfad, der führende
 * Nullen immer wegräumt, macht das Feld beim Tippen unberechenbar (die eigene
 * Eingabe verschwindet unter den Fingern) und zerstört Zwischenstände wie „0,"
 * auf dem Weg zu „0,5". „007" bleibt deshalb stehen und wird erst beim Blur
 * über `parseEur` zu 7.
 *
 * Erwartet den Feldinhalt NACH dem Einfügen (so, wie ihn `onChange` liefert).
 */
export const dropLeadingZero = (
  raw: string,
  caret: number,
): { text: string; caret: number } => {
  if (caret === 2 && /^0\d$/.test(raw)) return { text: raw.slice(1), caret: 1 };
  return { text: raw, caret };
};
